/**
 * The five supported trigger types for abilities.
 * - OnDraw: Fires when the card is drawn (including overflow draws).
 * - OnAttack: Fires after target selection but before simultaneous combat damage.
 * - OnKill: Fires when the minion is credited with killing an enemy.
 * - OnCombatStart: Fires once for applicable battlefield minions before the first attack.
 * - OnDeath: Fires when the minion dies during combat.
 */
export type AbilityTriggerType = 'OnDraw' | 'OnAttack' | 'OnKill' | 'OnCombatStart' | 'OnDeath';

/**
 * Array of all valid trigger type values for iteration/validation.
 */
export const VALID_TRIGGER_TYPES: ReadonlyArray<AbilityTriggerType> = Object.freeze([
  'OnDraw',
  'OnAttack',
  'OnKill',
  'OnCombatStart',
  'OnDeath',
]);

/**
 * Checks whether a string is a valid ability trigger type.
 * @param type The string to validate.
 * @returns true if the string matches one of the five supported trigger types.
 */
export function isValidTriggerType(type: string): type is AbilityTriggerType {
  return VALID_TRIGGER_TYPES.includes(type as AbilityTriggerType);
}

/**
 * A single ability definition referenced by a stable ID.
 */
export interface AbilityDefinition {
  /** Stable unique identifier for this ability (e.g., "lifesteal", "taunt"). */
  id: string;

  /** Human-readable name of the ability. */
  name: string;

  /** Description of what the ability does. */
  description: string;

  /** The trigger type that fires this ability (e.g., "OnDraw", "OnAttack"). */
  triggerType: AbilityTriggerType;

  /**
   * Type classifications for this ability. Unlike minions (which have one type),
   * abilities can have multiple types (e.g., ["healer", "bard"]).
   */
  types: string[];

  /**
   * Minimum minion level at which this ability can be randomly chosen during
   * an upgrade. A value of 0 means the ability is available at any level.
   * When leveling up, only abilities whose availableAtLevel <= minion.getLevel()
   * are eligible (in addition to matching type and not already owned).
   */
  availableAtLevel: number;
}

/**
 * Central registry for ability definitions.
 *
 * Abilities are referenced by stable IDs from minion definitions and upgrade
 * branches. The registry is the single source of truth for ability metadata
 * and validates that every ability ID used in the game is registered.
 */
export class AbilityRegistry {
  private abilities: Map<string, AbilityDefinition> = new Map();

  /**
   * Registers a single ability definition.
   * @param definition The ability to register.
   * @throws Error if an ability with the same ID is already registered.
   */
  public register(definition: AbilityDefinition): void {
    if (this.abilities.has(definition.id)) {
      throw new Error(`Ability "${definition.id}" is already registered`);
    }
    if (!Array.isArray(definition.types)) {
      throw new Error(`Ability "${definition.id}" must have a types array`);
    }
    this.abilities.set(definition.id, definition);
  }

  /**
   * Registers multiple ability definitions at once.
   * @param definitions Array of ability definitions to register.
   * @throws Error if any ability ID is a duplicate (including duplicates within the batch).
   */
  public registerMany(definitions: AbilityDefinition[]): void {
    for (const def of definitions) {
      this.register(def);
    }
  }

  /**
   * Resolves an ability definition by its stable ID.
   * @param id The ability ID to look up.
   * @returns The AbilityDefinition if found.
   * @throws Error if the ability ID is not registered.
   */
  public resolve(id: string): AbilityDefinition {
    const definition = this.abilities.get(id);
    if (!definition) {
      throw new Error(`Unknown ability ID: "${id}"`);
    }
    return definition;
  }

  /**
   * Safely resolves an ability definition by its stable ID.
   * @param id The ability ID to look up.
   * @returns The AbilityDefinition if found, or null if not registered.
   */
  public tryResolve(id: string): AbilityDefinition | null {
    return this.abilities.get(id) ?? null;
  }

  /**
   * Checks whether an ability ID is registered.
   * @param id The ability ID to check.
   * @returns true if the ability is registered.
   */
  public has(id: string): boolean {
    return this.abilities.has(id);
  }

  /**
   * Returns all registered ability IDs.
   */
  public getIds(): string[] {
    return [...this.abilities.keys()];
  }

  /**
   * Returns all registered ability definitions.
   */
  public getAll(): AbilityDefinition[] {
    return [...this.abilities.values()];
  }

  /**
   * Validates an array of ability IDs against the registry.
   * @param abilityIds Array of ability IDs to validate.
   * @returns true if all IDs are registered.
   * @throws Error if any ID is not registered, with details on the first unknown ID.
   */
  public validateAbilityIds(abilityIds: string[]): boolean {
    for (const id of abilityIds) {
      if (!this.abilities.has(id)) {
        throw new Error(`Unknown ability ID: "${id}"`);
      }
    }
    return true;
  }

  /**
   * Safely validates an array of ability IDs without throwing.
   * @param abilityIds Array of ability IDs to validate.
   * @returns Array of unknown (unregistered) ability IDs. Empty if all valid.
   */
  public findUnknownAbilityIds(abilityIds: string[]): string[] {
    const unknown: string[] = [];
    for (const id of abilityIds) {
      if (!this.abilities.has(id)) {
        unknown.push(id);
      }
    }
    return unknown;
  }

  /**
   * Clears all registered abilities. Useful for testing.
   */
  public clear(): void {
    this.abilities.clear();
  }
}
